import { useMemo } from 'react';
import { Select } from './Select';
import { useCurrencies } from '../../providers/ReferenceDataProvider';
import { isPreferredCurrency } from '../../providers/preferred-reference-data';
import type { GlobalSize } from '../../types';
import type { TestIdProps } from '../../utils';

export interface CurrencySelectorProps extends TestIdProps {
  id?: string;
  /** ISO 4217 currency code (case-insensitive); empty/null clears the selection. */
  value: string | null | undefined;
  /** Called with the uppercased ISO code (or `''` when the unselected option is picked). */
  onChange: (code: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Open the dropdown on mount — useful when used as an inline editor. */
  defaultOpen?: boolean;
  /** @default false */
  isSearchable?: boolean;
  height?: GlobalSize;
  className?: string;
  hasError?: boolean;
  /** Label for an extra "no selection" row at the top of the list. */
  unselectedOptionLabel?: string;
  /** Placeholder shown when empty — also the searchable input's placeholder. */
  placeholder?: string;
}

/**
 * Dropdown for ISO 4217 currency codes. Options come from
 * `ReferenceDataProvider` (GraphQL-fed in apps, fixture-fed in Storybook), with
 * preferred currencies bubbling to the top.
 */
export function CurrencySelector({
  id,
  testId,
  value,
  onChange,
  onBlur,
  disabled,
  autoFocus,
  defaultOpen,
  isSearchable = false,
  height,
  className,
  hasError,
  unselectedOptionLabel,
  placeholder
}: CurrencySelectorProps) {
  const currencies = useCurrencies();
  const options = useMemo(
    () =>
      currencies.map(c => ({
        value: c.code,
        label: c.symbol && c.symbol !== c.code ? `${c.code} (${c.symbol})` : c.code,
        preferred: isPreferredCurrency(c.code)
      })),
    [currencies]
  );

  const current = value == null ? '' : String(value).toUpperCase();

  return (
    <Select
      id={id}
      testId={testId}
      isSearchable={isSearchable}
      defaultOpen={defaultOpen}
      options={options}
      value={current}
      UnselectedOption={unselectedOptionLabel}
      onChange={next => {
        const picked = Array.isArray(next) ? next[0] : next;
        onChange(picked == null ? '' : String(picked).toUpperCase());
      }}
      onBlur={onBlur}
      autoFocus={autoFocus}
      disabled={disabled}
      height={height}
      className={className}
      hasError={hasError}
      buttonLabel={placeholder}
    />
  );
}

CurrencySelector.displayName = 'CurrencySelector';
